import { aiTask, apiPath, flush, track } from './track.js';
import { encode, type Snapshot } from './persisted.js';
import type { AppState } from './store.js';

/**
 * Синхронизация с сервером.
 *
 * Приложение остаётся local-first: без аккаунта оно полностью работоспособно,
 * расчёты идут на устройстве, сеть нужна только чтобы перенести состояние
 * между устройствами и не потерять его при чистке браузера.
 *
 * Модель — снимок целиком с версией. Для одного человека с двумя устройствами
 * это правильный размер решения: построчная синхронизация и CRDT здесь были бы
 * преждевременны, а расхождение версий разрешает человек, а не молчаливое
 * «победил последний».
 */

export interface User { id: string; email: string }

export interface RemoteState {
  version: number;
  state: SyncPayload | null;
  updatedAt: string | null;
}

/**
 * Что уезжает на сервер: конверт с версией схемы и сохраняемым состоянием.
 *
 * Состав полей больше не перечисляется здесь руками — он выводится из
 * `PersistedState`, и забытое поле ловит компилятор (см. `persisted.ts`).
 */
export type SyncPayload = Snapshot;

export class SyncError extends Error {}
export interface Conflict { conflict: true; version: number; state: SyncPayload | null }

/**
 * Есть ли связь с сервером — по любому запросу к нему.
 *
 * Значок в шапке главной слушает это. Прежде у него была своя проверка, а у
 * листа записи — своя, и они спорили: лист говорил «Нужна сеть», а значка
 * не было (владелец, 15.10.2026). Сорвался любой запрос — значок, пришёл
 * ответ — значок уходит.
 */
const reachListeners = new Set<(ok: boolean) => void>();
export function onServerReach(listener: (ok: boolean) => void): () => void {
  reachListeners.add(listener);
  return () => { reachListeners.delete(listener); };
}
function reach(ok: boolean): void {
  for (const listener of reachListeners) listener(ok);
}

/**
 * Общий доступ к своему /api. Открыт наружу, потому что им пользуется и разбор
 * фразы: ключ модели живёт только на сервере, и клиент разговаривает со своим
 * сервером, а не с провайдером.
 */
export const api = async (path: string, init?: RequestInit): Promise<Response> => {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      ...init,
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    });
  } catch (e) {
    // Отменили сами — это не потеря связи и не сбой.
    if (!(e instanceof DOMException && e.name === 'AbortError')) {
      noteFailure(path, 0);
      reach(false);
    }
    throw e;
  }
  // 502–504 — отвечает прокси, а за ним никого: для человека это та же
  // «нет связи с сервером».
  reach(res.status < 502 || res.status > 504);
  if (res.status >= 500 || (aiTask(path) && !res.ok && res.status !== 401 && res.status !== 402 && res.status !== 403 && res.status !== 429)) {
    noteFailure(path, res.status);
  } else if (res.status === 402 || res.status === 429) {
    // Сервер отказал по тарифу или квоте — это упор в Pro, а не сбой.
    track('pro_wall', { feature: aiTask(path) ?? apiPath(path), reason: res.status === 429 ? 'quota' : 'tier' });
  }
  return res;
};

/**
 * Сбой сервера — в отчёт владельцу: адрес без номеров и код ответа. У ИИ
 * отдельной строкой, что именно не разобралось. Отказ по тарифу и квоте —
 * не сбой: его отчёт видит как упор в Pro.
 */
function noteFailure(path: string, status: number): void {
  const task = aiTask(path);
  if (task) track('ai_failed', { task, status });
  else track('api_error', { path: apiPath(path), status });
}

/**
 * Ответ сервера, который не JSON, — это страница прокси, а не наш ответ.
 *
 * Так бывает, когда nginx не дождался разбора (504), не принял тело (413)
 * или сервер недоступен (502). Прежде человек видел «JSON Parse error:
 * Unrecognized token '<'» — сообщение о нашей внутренности, а не о том, что
 * случилось и что делать.
 */
const proxyMessage = (status: number): string => (
  status === 504 ? 'Сервер не успел ответить — попробуй ещё раз. Длинный документ можно загрузить по страницам'
  : status === 413 ? 'Файл слишком большой для отправки — уменьши его или загрузи по страницам'
  : status === 502 || status === 503 ? 'Сервер сейчас недоступен — попробуй через минуту'
  : `Сервер ответил ошибкой ${status} — попробуй ещё раз`);

export const unwrap = async (res: Response): Promise<unknown> => {
  const text = await res.text();
  let body: unknown = {};
  if (text) {
    try { body = JSON.parse(text); } catch { throw new SyncError(proxyMessage(res.status)); }
  }
  if (!res.ok) {
    const message = typeof body === 'object' && body && 'error' in body
      ? String((body as { error: unknown }).error)
      : `Ошибка ${res.status}`;
    throw new SyncError(message);
  }
  return body;
};

const device = (): string => {
  const ua = navigator.userAgent;
  if (/iPhone|iPad/.test(ua)) return 'iPhone';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac/.test(ua)) return 'Mac';
  return 'браузер';
};

export async function register(email: string, password: string): Promise<User> {
  const body = await unwrap(await api('/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, device: device() }),
  })) as { user: User };
  track('auth_register');
  return body.user;
}

export async function login(email: string, password: string): Promise<User> {
  const body = await unwrap(await api('/login', {
    method: 'POST',
    body: JSON.stringify({ email, password, device: device() }),
  })) as { user: User };
  track('auth_login');
  return body.user;
}

/**
 * Работает ли сброс пароля на сервере.
 *
 * Без почты письмо со ссылкой уйти не может, и «Забыли пароль?» не
 * показывается: кнопка, которая ничего не сделает, хуже её отсутствия.
 */
export async function passwordResetStatus(): Promise<'available' | 'unavailable' | 'offline'> {
  try {
    const body = await unwrap(await api('/password-reset')) as { available?: boolean };
    return body.available === true ? 'available' : 'unavailable';
  } catch {
    return 'offline';
  }
}

export async function passwordResetAvailable(): Promise<boolean> {
  return await passwordResetStatus() === 'available';
}

/** Прислать ссылку для нового пароля. Ответ одинаков для любой почты. */
export async function requestPasswordReset(email: string): Promise<void> {
  await unwrap(await api('/password-reset/request', {
    method: 'POST',
    body: JSON.stringify({ email }),
  }));
}

/** Задать новый пароль по ссылке. Сервер сразу впускает человека. */
export async function confirmPasswordReset(token: string, password: string): Promise<User> {
  const body = await unwrap(await api('/password-reset/confirm', {
    method: 'POST',
    body: JSON.stringify({ token, password, device: device() }),
  })) as { user: User };
  return body.user;
}

export async function logout(): Promise<void> {
  // До выхода: после него событие ушло бы уже без аккаунта.
  track('auth_logout');
  await flush();
  await unwrap(await api('/logout', { method: 'POST' }));
}

/**
 * Кто вошёл. `null` — никто; ошибка — сервер не ответил или нет сети.
 *
 * Раньше ошибка тоже давала `null`: занятый сервер (503) или обрыв выглядели
 * как «вышел из аккаунта», и приложение переставало отправлять данные.
 */
export async function me(): Promise<User | null> {
  const body = await unwrap(await api('/me')) as { user: User | null };
  return body.user;
}

/** Что ответил сервер на удаление аккаунта. */
export interface DeleteResult {
  deleted: boolean;
  /** Что удалено — для ответа человеку его же словами. */
  removed: Record<string, boolean>;
  /** Что осталось и почему. */
  kept: Record<string, string>;
  /** Чем остановлены будущие списания: 'none' | 'local' | 'provider'. */
  renewalStop: string;
  /** Подключён ли приём платежей. Пока нет — и это говорится прямо. */
  billingConnected: boolean;
  /** Что с деньгами: одной фразой, без обещаний, которых некому исполнить. */
  money: string;
}

/**
 * Удалить аккаунт.
 *
 * Пароль спрашивается не для строгости: действие необратимо, и чужой доступ
 * к открытой вкладке не должен стоить человеку всех его данных.
 */
export async function deleteAccount(password: string): Promise<DeleteResult> {
  const result = await unwrap(await api('/account/delete', {
    method: 'POST',
    body: JSON.stringify({ password }),
  })) as DeleteResult;
  // Без аккаунта: его события удалены вместе с ним, остаётся факт ухода.
  track('account_deleted');
  return result;
}

/**
 * Подписка на рассылку — копия согласия на сервере (apps/api/src/marketing.mjs).
 * `confirmed` — почта подтверждена кнопкой в письме; до этого писем нет.
 */
export interface Marketing { granted: boolean; confirmed: boolean; sent?: boolean }
export async function getMarketing(): Promise<Marketing> {
  return await unwrap(await api('/marketing')) as Marketing;
}
export async function setMarketing(granted: boolean, version: string): Promise<Marketing> {
  return await unwrap(await api('/marketing', { method: 'POST', body: JSON.stringify({ granted, version }) })) as Marketing;
}

export async function pull(): Promise<RemoteState> {
  return await unwrap(await api('/state')) as RemoteState;
}

/**
 * Отправка снимка.
 *
 * При расхождении версий сервер отвечает отказом и своим состоянием — вместо
 * молчаливой перезаписи. Решение принимает человек: взять серверное или
 * отправить своё поверх.
 */
export async function push(
  payload: SyncPayload,
  baseVersion: number,
  force = false,
): Promise<{ version: number } | Conflict> {
  const res = await api('/state', {
    method: 'PUT',
    body: JSON.stringify({ state: payload, baseVersion, force, device: device() }),
  });
  if (res.status === 409) return await res.json() as Conflict;
  return await unwrap(res) as { version: number };
}

/**
 * Отпечаток снимка — чтобы узнать на сервере свою же версию.
 *
 * Сервер хранит снимок в jsonb и меняет порядок ключей, поэтому отпечаток
 * снимается с канонической записи: ключи по алфавиту на каждом уровне.
 * Время сохранения (`savedAt`) в отпечаток не входит — только данные.
 */
export async function snapshotPrint(state: unknown): Promise<string> {
  const canon = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(canon);
    if (v && typeof v === 'object') {
      return Object.fromEntries(Object.keys(v as Record<string, unknown>).sort()
        .filter((k) => (v as Record<string, unknown>)[k] !== undefined)
        .map((k) => [k, canon((v as Record<string, unknown>)[k])]));
    }
    return v;
  };
  const bytes = new TextEncoder().encode(JSON.stringify(canon(state)));
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Снимок из состояния приложения.
 *
 * Вычисляемые поля (`today`, `isEmpty`) отсеиваются самим кодеком: их нет в
 * `PersistedState`, а значит, они не могут попасть на сервер по недосмотру.
 */
export const toPayload = (s: AppState): SyncPayload => encode({
  accounts: s.accounts,
  creditTerms: s.creditTerms,
  loans: s.loans,
  incomeStreams: s.incomeStreams,
  obligations: s.obligations,
  goals: s.goals,
  goalEvents: s.goalEvents,
  plannedExpenses: s.plannedExpenses,
  plannedAllocations: s.plannedAllocations,
  expectedIncomes: s.expectedIncomes,
  ...(s.trackingStartedAt ? { trackingStartedAt: s.trackingStartedAt } : {}),
  reserves: s.reserves,
  stablePlans: s.stablePlans,
  stablePayouts: s.stablePayouts,
  stableMoves: s.stableMoves,
  transactions: s.transactions,
  receiptImports: s.receiptImports,
  categories: s.categories,
  ...(s.categoryDefaultsReady !== undefined ? { categoryDefaultsReady: s.categoryDefaultsReady } : {}),
  acceptedPlans: s.acceptedPlans,
  periodPins: s.periodPins,
  periodBaselines: s.periodBaselines,
  dismissedHints: s.dismissedHints,
  periodDeals: s.periodDeals,
  periodTopicsOff: s.periodTopicsOff,
  periodReviewLater: s.periodReviewLater,
  allocations: s.allocations,
  consents: s.consents,
  insightLog: s.insightLog,
  // В снимок уезжают значения со статусом, а не числа: иначе «не знаю»
  // на другом устройстве превратилось бы в подтверждённый ноль.
  // Оценка человека, а не «обычно» по истории: посчитанное число, уехав
  // в снимок, стало бы оценкой и начало бы подкручивать само себя.
  needBaseline: s.estimates.need,
  wantBaseline: s.estimates.want,
  usualPinned: s.usualPinned,
  accountGroups: s.accountGroups,
  ...(s.profileName ? { profileName: s.profileName } : {}),
  ...(s.focusOverride ? { focusOverride: s.focusOverride } : {}),
  ...(s.planMode ? { planMode: s.planMode } : {}),
  ...(s.tradeoffChoice ? { tradeoffChoice: s.tradeoffChoice } : {}),
  ...(s.newCategoryMode ? { newCategoryMode: s.newCategoryMode } : {}),
  ...(s.periodChoice ? { periodChoice: s.periodChoice } : {}),
}, s.snapshotUnknown);
